/**
 * Glossary (UX-005 §3). Plain-language definitions of technical terms, served
 * once from the backend and cached. Referenced by figure.plain fields (the key
 * column). Read-only, searchable.
 */
import { useState, useMemo, type ReactNode } from "react";
import { useGetV1Glossary } from "@solarkey/api-client";
import { Skeleton, ErrorState, EmptyState } from "@solarkey/ui";
import { BackButton } from "../shell/BackButton";

export function GlossaryScreen(): ReactNode {
  const query = useGetV1Glossary();
  const [filter, setFilter] = useState("");

  const entries = useMemo(() => {
    const all = query.data?.data.data?.entries ?? [];
    if (!filter) return all;
    const lc = filter.toLowerCase();
    return all.filter(
      (e) =>
        (e.term?.toLowerCase().includes(lc)) ||
        (e.plain?.toLowerCase().includes(lc)) ||
        (e.key?.toLowerCase().includes(lc))
    );
  }, [query.data, filter]);

  if (query.isLoading) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <BackButton />
        <h1 style={h1}>Glossary</h1>
      </div>
        <Skeleton variant="text" lines={8} />
      </div>
    );
  }

  if (query.isError || !query.data) {
    return (
      <ErrorState
        title="Couldn't load glossary"
        onRetry={() => void query.refetch()}
      />
    );
  }

  const locale = query.data.data.data?.locale;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <BackButton />
        <h1 style={h1}>Glossary</h1>
      </div>
        {locale ? <span style={localeBadge}>{locale}</span> : null}
      </div>

      <input
        type="search"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        placeholder="Filter terms…"
        aria-label="Filter glossary"
        style={searchInput}
      />

      {entries.length === 0 ? (
        <EmptyState
          variant="no_data"
          title="No matching terms"
          message={filter ? `Nothing matched "${filter}".` : "The glossary is empty."}
        />
      ) : (
        <dl style={defList}>
          {entries.map((e, i) => (
            <div key={e.key ?? i} style={defRow}>
              <dt style={term}>{e.term ?? e.key}</dt>
              <dd style={definition}>{e.plain ?? ""}</dd>
              {e.key ? <code style={keyCode}>{e.key}</code> : null}
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}

const h1: React.CSSProperties = {
  margin: 0,
  font: "600 20px/1.25 var(--sk-font-text)",
  color: "var(--sk-color-ink-1)",
};
const localeBadge: React.CSSProperties = {
  font: "400 11px/1.3 var(--sk-font-mono)",
  color: "var(--sk-color-ink-3)",
  border: "1px solid var(--sk-color-border-2)",
  borderRadius: "var(--sk-radius-card)",
  padding: "2px 6px",
};
const searchInput: React.CSSProperties = {
  padding: "8px 12px",
  font: "400 14px/1.5 var(--sk-font-text)",
  border: "1px solid var(--sk-color-border-2)",
  borderRadius: "var(--sk-radius-card)",
  outline: "none",
  color: "var(--sk-color-ink-1)",
  background: "var(--sk-color-surface-1)",
};
const defList: React.CSSProperties = {
  margin: 0,
  display: "flex",
  flexDirection: "column",
  gap: 1,
};
const defRow: React.CSSProperties = {
  padding: "10px 12px",
  borderRadius: "var(--sk-radius-card)",
  border: "1px solid var(--sk-color-border-1)",
  display: "flex",
  flexDirection: "column",
  gap: 3,
};
const term: React.CSSProperties = {
  font: "600 14px/1.35 var(--sk-font-text)",
  color: "var(--sk-color-ink-1)",
};
const definition: React.CSSProperties = {
  margin: 0,
  font: "400 13px/1.45 var(--sk-font-text)",
  color: "var(--sk-color-ink-2)",
};
const keyCode: React.CSSProperties = {
  font: "400 10px/1.3 var(--sk-font-mono)",
  color: "var(--sk-color-ink-3)",
};
